import { ContactShadows, Float, PerspectiveCamera } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import Crane from 'components/molecules/3d/Crane';
import Dragon from 'components/molecules/3d/Dragon';
import Fox from 'components/molecules/3d/Fox';
// import { EffectComposer, SSAO } from '@react-three/postprocessing';
import gsap from 'gsap';
import type { ReactElement, Ref } from 'react';
import { Suspense, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import Koi from '../../../../molecules/3d/Koi';
// import Kitsune from '../3d/Kitsune';

import { reMap } from '../../../../../util/math.utils';
import type { AnimalType } from '../../ServicePowers.data';
import type { SceneProps, SceneWebglRef } from './Scene.data';

// softShadows();

export default function Scene({
  slides,
  ref,
}: SceneProps & { ref?: Ref<SceneWebglRef> }): ReactElement {
  // const floorRef = useRef<MeshReflectorMaterial>();
  const progress = useRef(0);
  const currentSlide = useRef(0);
  const modelRef = useRef<THREE.Group>(null);
  const backgroundRef = useRef<THREE.Color>(undefined);
  const dirLight = useRef<THREE.DirectionalLight>(null);
  const spotLight = useRef<THREE.SpotLight>(null);
  const color = new THREE.Color();
  const fogRef = useRef<THREE.Fog>(undefined);

  const slideAnimalsRef = useRef<Array<THREE.Group | null>>([]);
  const slideMouseRef = useRef<Array<THREE.Group | null>>([]);
  // const slideTexts = useRef<Array<THREE.Group | null>>([]);

  const progressSlideData = useRef<
    Array<{
      id: AnimalType;
      progress: number;
    }>
  >([]);

  const setProgress = useCallback((value: number) => {
    progress.current = value;
  }, []);

  useEffect(() => {
    slides.forEach((element, i) => {
      if (slideAnimalsRef.current[i]) {
        slideAnimalsRef.current[i]!.position.y = 20;
      }
    });
  }, [slides]);

  useEffect(() => {
    if (dirLight.current) {
      dirLight.current.target.position.set(0, 0, -20);
      dirLight.current.target.updateMatrixWorld();
    }
    if (spotLight.current) {
      spotLight.current.target.position.set(0, 0, -20);
    }
  }, [dirLight, spotLight]);

  const setCurrentSlide = useCallback((slide: number) => {
    if (currentSlide.current !== slide) {
      const prevSlide = currentSlide.current;
      currentSlide.current = slide;
      if (modelRef.current && slideAnimalsRef.current[currentSlide.current]) {
        const timeline = gsap.timeline({});
        timeline.to(
          slideAnimalsRef.current[slide]!.rotation,
          {
            y: prevSlide > currentSlide.current ? '+=3' : '-=3',
            duration: 1,
            ease: 'Power2.easeInOut',
          },
          0,
        );
      }
    }
  }, []);

  const setProgressData = useCallback(
    (
      progressData: Array<{
        id: AnimalType;
        progress: number;
      }>,
    ) => {
      // console.log(
      //   progressData,
      //   // 'setProgressData',
      //   // progressData.map((animal) => `${animal.id}:${animal.progress}`),
      // );
      progressSlideData.current = progressData;
    },
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      setProgress,
      setProgressData,
      setCurrentSlide,
    }),
    [setProgressData, setProgress, setCurrentSlide],
  );

  useFrame(() => {
    // state.camera.lookAt(0, 0, 0);
    if (progress.current > 0 && progress.current < 1) {
      if (modelRef.current && progressSlideData.current[0]?.progress) {
        const curSlideRef = slideAnimalsRef.current[currentSlide.current]!;
        const { progress } = progressSlideData.current[currentSlide.current];

        let aProgress = 0;
        const aStart = 0.3;
        if (progress >= aStart) aProgress = reMap(progress, aStart, 1, 0, 1);

        if (aProgress < 0.3) {
          curSlideRef.position.y = reMap(aProgress, 0, 0.3, -7, -0.6);
          curSlideRef.position.z = reMap(aProgress, 0, 0.3, 20, 3);
          curSlideRef.rotation.y = reMap(aProgress, 0, 0.3, 0, Math.PI * 1.5);
        }
        if (aProgress >= 0.3 && aProgress <= 0.7) {
          curSlideRef.position.y = reMap(aProgress, 0.3, 0.7, -0.61, 0.6);
          curSlideRef.position.z = reMap(aProgress, 0.3, 0.7, 3, -3);
          curSlideRef.rotation.y = reMap(aProgress, 0.3, 0.7, Math.PI * 1.5, Math.PI * 2);
        }
        if (aProgress > 0.7) {
          curSlideRef.position.y = reMap(aProgress, 0.7, 1, 0.6, 12);
          curSlideRef.position.z = reMap(aProgress, 0.7, 1, -3, -10);
          curSlideRef.rotation.y = reMap(aProgress, 0.7, 1, Math.PI * 2, Math.PI * 3.5);
        }
      }

      if (fogRef.current)
        fogRef.current?.color.lerp(color.set(slides[currentSlide.current].backgroundColor), 0.05);

      if (backgroundRef.current) {
        if (backgroundRef.current.r.toFixed(6) !== color.r.toFixed(6)) {
          backgroundRef.current?.lerp(
            color.set(slides[currentSlide.current].backgroundColor),
            0.05,
          );
        }
      }
    }
  });

  return (
    <>
      <Suspense fallback={null}>
        <PerspectiveCamera fov={30} position={[0, 0, 20]} makeDefault />

        {/* <color ref={backgroundRef} attach="background" args={[slides[0].backgroundColor]} /> */}
        {/* {fog && <fog ref={fogRef} attach="fog" args={[slides[0].backgroundColor, 18, 25]} />} */}

        <ambientLight intensity={0.1} />
        <directionalLight position={[-20, 20, 20]} intensity={0.4} />
        <directionalLight position={[-20, -20, -20]} intensity={0.2} color={color} />
        <pointLight position={[0, 0, 5]} distance={5} intensity={1} color={color} />
        <pointLight position={[14, 10, -5]} distance={200} intensity={1.8} />

        <group ref={modelRef} position={[0, 1, 0]} rotation={[0, 0, 0]}>
          {slides.map((slide, i) => (
            <group key={slide.id.concat('-animal')}>
              <group
                position={[0, -10, 0]}
                ref={(element) => {
                  slideAnimalsRef.current[i] = element;
                }}
              >
                <group
                  ref={(element) => {
                    slideMouseRef.current[i] = element;
                  }}
                >
                  <Float
                    position={[0, 0, 0]}
                    speed={2}
                    rotationIntensity={0.2}
                    floatIntensity={1.5}
                  >
                    {
                      {
                        kitsune: (
                          <Fox
                            position={[1.5, -3, 0]}
                            rotation={[0, Math.PI / 3, 0]}
                            color={slide.animalColor}
                          />
                        ),
                        dragon: <Dragon position={[0, 0, 0]} color={slide.animalColor} />,
                        koi: (
                          <Koi
                            scale={[0.8, 0.8, 0.8]}
                            position={[0, 0, 0]}
                            rotation={[Math.PI / 10, 0, 0]}
                            color={slide.animalColor}
                          />
                        ),
                        crane: (
                          <Crane
                            position={[0, 3, 0]}
                            scale={[0.8, 0.8, 0.8]}
                            color={slide.animalColor}
                          />
                        ),
                      }[slide.id]
                    }
                  </Float>
                </group>
              </group>
            </group>
          ))}
        </group>

        <ContactShadows
          position={[0, -2.5, 0]}
          resolution={1024}
          opacity={0.25}
          blur={0.7}
          width={100}
          height={100}
          far={10}
          smooth
          scale={1}
        />
      </Suspense>
    </>
  );
}
